import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import React from "react";

import classNames from "@calcom/ui/classNames";

import { Icon } from "../icon";
import type { IconName } from "../icon";

export const badgeStyles = cva("font-semibold inline-flex items-center justify-center rounded-full gap-x-1 border transition-colors duration-200", {
  variants: {
    variant: {
      default: "bg-orange-500 text-white border-orange-600 hover:bg-orange-600",
      warning: "bg-orange-500 text-white border-orange-600 hover:bg-orange-600",
      orange: "bg-orange-500 text-white border-orange-600 hover:bg-orange-600",
      yellow: "bg-yellow-500 text-white border-yellow-600 hover:bg-yellow-600",
      success: "bg-green-500 text-white border-green-600 hover:bg-green-600",
      green: "bg-green-500 text-white border-green-600 hover:bg-green-600",
      gray: "bg-gray-500 text-white border-gray-600 hover:bg-gray-600",
      blue: "bg-blue-500 text-white border-blue-600 hover:bg-blue-600",
      red: "bg-red-500 text-white border-red-600 hover:bg-red-600",
      error: "bg-red-500 text-white border-red-600 hover:bg-red-600",
      grayWithoutHover: "bg-gray-500 text-white border-gray-600",
      purple: "bg-purple-500 text-white border-purple-600 hover:bg-purple-600",
    },
    size: {
      sm: "px-2 py-0.5 text-[10px] leading-tight",
      md: "px-2.5 py-1 text-xs leading-tight",
      lg: "px-3 py-1.5 text-sm leading-tight",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

type InferredBadgeStyles = VariantProps<typeof badgeStyles>;

type IconOrDot =
  | {
      startIcon?: IconName;
      withDot?: never;
      customDot?: never;
    }
  | { startIcon?: never; withDot?: true; customDot?: never }
  | { startIcon?: never; withDot?: never; customDot?: React.ReactNode };

export type BadgeBaseProps = InferredBadgeStyles & {
  children: React.ReactNode;
  rounded?: boolean;
  customStartIcon?: React.ReactNode;
  customDot?: React.ReactNode;
} & IconOrDot;

export type BadgeProps =
  /**
   * This union type helps TypeScript understand that there's two options for this component:
   * Either it's a div element on which the onClick prop is not allowed, or it's a button element
   * on which the onClick prop is required. This is because the onClick prop is used to determine
   * whether the component should be a button or a div.
   */
  | (BadgeBaseProps & Omit<React.HTMLAttributes<HTMLDivElement>, "onClick"> & { onClick?: never })
  | (BadgeBaseProps & Omit<React.HTMLAttributes<HTMLButtonElement>, "onClick"> & { onClick: () => void });

export const Badge = function Badge(props: BadgeProps) {
  const {
    customStartIcon,
    variant,
    className,
    size,
    startIcon,
    withDot,
    children,
    rounded,
    customDot,
    ...passThroughProps
  } = props;
  const isButton = "onClick" in passThroughProps && passThroughProps.onClick !== undefined;
  const StartIcon = startIcon;
  const classes = classNames(
    badgeStyles({ variant, size }),
    rounded && "h-5 w-5 rounded-full p-0",
    className
  );

  const Children = () => (
    <>
      {withDot ? (
        <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" data-testid="go-primitive-dot">
          <circle cx="4" cy="4" r="4" />
        </svg>
      ) : null}
      {customStartIcon ||
        (StartIcon ? (
          <Icon
            name={StartIcon}
            data-testid="start-icon"
            className="stroke-[3px]"
            style={{ width: 12, height: 12 }}
          />
        ) : null)}
      {children}
    </>
  );

  const Wrapper = isButton ? "button" : "div";

  return React.createElement(Wrapper, { ...passThroughProps, className: classes }, <Children />);
};
